import { audio } from '../systems/audio/AudioSystem.js';

/** Audio/accessibility settings, persisted via SaveManager. */
export class SettingsMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    this.el.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'SETTINGS';
    this.el.appendChild(title);

    const rows = document.createElement('div');
    rows.className = 'settings-rows';
    rows.appendChild(this._buildSliderRow('SFX Volume', this.save.sfxVolume, (v) => {
      this.save.setSfxVolume(v);
      audio.setSfxVolume(v);
    }));
    rows.appendChild(this._buildSliderRow('Music Volume', this.save.musicVolume, (v) => {
      this.save.setMusicVolume(v);
      audio.setMusicVolume(v);
    }));
    // Split out of the old single "Reduce Shake/Flash" switch: camera shake and
    // screen flashes cover two unrelated conditions (vestibular vs
    // photosensitivity), and one switch forced a player who needed either to
    // give up both. Both default on.
    rows.appendChild(this._buildCheckboxRow('Reduce Screen Shake', this.save.reduceShake, (checked) => {
      this.save.setReduceShake(checked);
    }));
    rows.appendChild(this._buildCheckboxRow('Reduce Flashing', this.save.reduceFlash, (checked) => {
      this.save.setReduceFlash(checked);
    }));
    // Presentational motion only — kept separate from the two above because it
    // mitigates nothing, it's just a speed preference. Defaults off.
    rows.appendChild(this._buildCheckboxRow('Skip Animations', this.save.skipAnimations, (checked) => {
      this.save.setSkipAnimations(checked);
    }));
    // Slows hostiles only (Game._update's enemyDt), so the player stays fully
    // responsive instead of the whole game feeling sluggish. Worded as the
    // effect rather than as a difficulty label — it is an assist, not a
    // lesser mode. Takes effect immediately, mid-run included.
    rows.appendChild(this._buildSliderRow('Enemy Speed', this.save.enemySpeed, (v) => {
      this.save.setEnemySpeed(v);
    }, { min: 0.5, max: 1, step: 0.05 }));
    // Focus is held for a whole pattern and dash is held to auto-dash; both
    // are sustained holds, which is the input shape that hurts most under
    // limited hand mobility. The label names the auto-dash consequence
    // outright rather than letting the player discover it mid-fight.
    rows.appendChild(this._buildCheckboxRow('Toggle Focus/Dash (tap instead of hold)', this.save.toggleHold, (checked) => {
      this.save.setToggleHold(checked);
    }));
    rows.appendChild(this._buildCheckboxRow('High Contrast Telegraphs', this.save.highContrast, (checked) => {
      this.save.setHighContrast(checked);
    }));
    // BULLET-HEAVEN.md §H2. Worded as what the player will feel rather than
    // what the code does: "rising" vs. the old batches. Takes effect on the
    // next wave stage, not mid-stage — Game reads it when it builds each one.
    rows.appendChild(this._buildCheckboxRow('Rising Waves', this.save.waveRamp, (checked) => {
      this.save.setWaveRamp(checked);
    }));
    // BULLET-HELL.md §D3. Worded as the trade the player makes rather than as
    // "deathbomb", which means nothing outside the genre. Read when the run
    // starts, so a mid-run change applies to the next one.
    rows.appendChild(this._buildCheckboxRow('Deathbomb (ultimate refunds a fresh hit)', this.save.deathbomb, (checked) => {
      this.save.setDeathbomb(checked);
    }));
    // ACT3-BIBLE.md §3. Named for the fight rather than for the mechanic, so a
    // player who has met the Manifold recognises it and one who hasn't isn't
    // spoiled by it. Read when the boss stage starts, so a mid-run change
    // applies to the next attempt rather than the current one.
    rows.appendChild(this._buildCheckboxRow('Manifold Assist (second body follows)', this.save.manifoldAssist, (checked) => {
      this.save.setManifoldAssist(checked);
    }));
    this.el.appendChild(rows);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  /**
   * @param {object} [range] - defaults to the 0..1 volume range. `min` is not
   *   always 0, so the --fill percentage has to be normalised against the
   *   span rather than against the raw value (a 0.5-min slider sitting at its
   *   floor should read as an empty track, not a half-full one).
   */
  _buildSliderRow(label, value, onChange, range = {}) {
    const { min = 0, max = 1, step = 0.05 } = range;
    const row = document.createElement('div');
    row.className = 'settings-row';

    const name = document.createElement('span');
    name.className = 'settings-row-name';
    name.textContent = label;
    row.appendChild(name);

    const readout = document.createElement('span');
    readout.className = 'settings-row-value';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'settings-slider';
    slider.min = String(min);
    slider.max = String(max);
    slider.step = String(step);
    slider.value = String(value);
    // --fill drives the gold filled-track portion in style.css (WebKit has
    // no styleable range-progress pseudo, so CSS can't derive it alone).
    const sync = () => {
      const v = Number(slider.value);
      const span = max - min;
      slider.style.setProperty('--fill', `${span > 0 ? ((v - min) / span) * 100 : 0}%`);
      readout.textContent = `${Math.round(v * 100)}%`;
    };
    sync();
    slider.addEventListener('input', () => {
      sync();
      onChange(Number(slider.value));
    });
    row.appendChild(readout);
    row.appendChild(slider);

    return row;
  }

  _buildCheckboxRow(label, checked, onChange) {
    const row = document.createElement('div');
    row.className = 'settings-row';

    const name = document.createElement('span');
    name.className = 'settings-row-name';
    name.textContent = label;
    row.appendChild(name);

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'settings-checkbox';
    checkbox.checked = checked;
    checkbox.addEventListener('change', () => onChange(checkbox.checked));
    row.appendChild(checkbox);

    return row;
  }

  destroy() {
    this.el.remove();
  }
}
